import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { StaffInfo, StaffRole } from '~/Services/StaffServices'

export interface StaffFilter {
  queryString?: string
  columnFilters?: Record<string, any>
}

export interface StaffSort {
  field: string
  order: 'ascend' | 'descend'
}

export interface StaffReducerStore {
  data: StaffInfo[]
  totalCount: number
  currentEntity: StaffInfo | null
  currentRoles: StaffRole[]
  loading: boolean
  error: string | null
  filter: StaffFilter
  keyword: string
  columnFilters: Record<string, any>
  page: number
  pageSize: number
  expand: string | null
  sort?: StaffSort
}

const initialState: StaffReducerStore = {
  data: [],
  totalCount: 0,
  currentEntity: null,
  currentRoles: [],
  loading: false,
  error: null,
  filter: {},
  keyword: '',
  columnFilters: {},
  page: 1,
  pageSize: 10,
  expand: null,
  sort: undefined,
}

export const StaffSlice = createSlice({
  name: 'staff',
  initialState,
  reducers: {
    // Get staff list
    getStaffList: (
      state,
      action: PayloadAction<{
        orgId: string
        page?: number
        pageSize?: number
        filter?: StaffFilter
        sort?: StaffSort
        expand?: string
      }>,
    ) => {
      state.loading = true
      state.error = null
      if (action.payload.page) state.page = action.payload.page
      if (action.payload.pageSize) state.pageSize = action.payload.pageSize
      if (action.payload.filter) state.filter = action.payload.filter
      if (action.payload.sort) state.sort = action.payload.sort
      if (action.payload.expand) state.expand = action.payload.expand
    },
    getStaffListSuccess: (
      state,
      action: PayloadAction<{ results: StaffInfo[]; queryCount: number }>,
    ) => {
      state.data = action.payload.results
      state.totalCount = action.payload.queryCount
      state.loading = false
      state.error = null
    },
    getStaffListFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    setKeyword: (state, action: PayloadAction<string>) => {
      state.keyword = action.payload
      state.page = 1
    },

    setColumnFilters: (state, action: PayloadAction<Record<string, any>>) => {
      state.columnFilters = action.payload
      state.page = 1
    },

    setSort: (state, action: PayloadAction<StaffSort | undefined>) => {
      state.sort = action.payload
    },

    // Clear error
    clearError: state => {
      state.error = null
    },

    // Reset filter
    resetFilter: state => {
      state.filter = {}
      state.keyword = ''
      state.columnFilters = {}
      state.sort = undefined
      state.page = 1
    },

    // Create new staff
    createStaff: (
      state,
      action: PayloadAction<{
        orgId: string
        data: any
        onSuccess?: (staff: StaffInfo) => void
        onFailure?: (error: string) => void
      }>,
    ) => {
      state.loading = true
      state.error = null
    },
    createStaffSuccess: (state, action: PayloadAction<StaffInfo>) => {
      state.loading = false
      state.error = null
    },
    createStaffFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Delete staff
    deleteStaff: (
      state,
      action: PayloadAction<{
        orgId: string
        userIds: string[]
        onSuccess?: () => void
        onFailure?: (error: string) => void
      }>,
    ) => {
      state.loading = true
      state.error = null
    },
    deleteStaffSuccess: (state, action: PayloadAction<string[]>) => {
      state.loading = false
      state.error = null
      state.data = state.data.filter(
        staff => action.payload.indexOf(staff.id) === -1
      );
      state.totalCount = Math.max(0, state.totalCount - action.payload.length)
    },
    deleteStaffFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Get roles of user
    getRoles: (
      state,
      action: PayloadAction<{
        userId: string
        orgId: string
      }>,
    ) => {
      state.loading = true
      state.error = null
    },
    getRolesSuccess: (state, action: PayloadAction<StaffRole[]>) => {
      state.loading = false
      state.error = null
      state.currentRoles = action.payload
    },
    getRolesFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Clear current roles
    clearCurrentRoles: state => {
      state.currentRoles = []
    },

    // Assign roles to user
    assignRoles: (
      state,
      _action: PayloadAction<{
        userId: string
        roleData: { organizationId: string; roleName: string }[]
        onSuccess?: () => void
        onFailure?: (error: string) => void
      }>,
    ) => {
      state.loading = true
      state.error = null
    },
    assignRolesSuccess: (state) => {
      state.loading = false
      state.error = null
    },
    assignRolesFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Update check-in date
    updateCheckInDate: (
      state,
      _action: PayloadAction<{
        userOrgId: string
        checkInDate: string
        onSuccess?: () => void
        onFailure?: (error: string) => void
      }>,
    ) => {
      state.loading = true
      state.error = null
    },
    updateCheckInDateSuccess: (state) => {
      state.loading = false
      state.error = null
    },
    updateCheckInDateFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Remove roles from user
    removeRoles: (
      state,
      action: PayloadAction<{
        userId: string
        roleData: { organizationId: string; roleName: string }[]
        onSuccess?: () => void
        onFailure?: (error: string) => void
      }>,
    ) => {
      state.loading = true
      state.error = null
    },
    removeRolesSuccess: (state) => {
      state.loading = false
      state.error = null
    },
    removeRolesFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Export all staffs
    exportAllStaffs: (
      state,
      action: PayloadAction<{
        orgId: string
        onSuccess?: (data: any) => void
        onFailure?: (error: string) => void
      }>,
    ) => {
      state.loading = true
      state.error = null
    },
    exportAllStaffsSuccess: (state) => {
      state.loading = false
      state.error = null
    },
    exportAllStaffsFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Export staffs in current page
    exportStaffsInCurrentPage: (
      state,
      action: PayloadAction<{
        orgId: string
        page?: number
        pageSize?: number
        filter?: StaffFilter
        sort?: StaffSort
        expand?: string
        onSuccess?: (data: any) => void
        onFailure?: (error: string) => void
      }>,
    ) => {
      state.loading = true
      state.error = null
    },
    exportStaffsInCurrentPageSuccess: (state) => {
      state.loading = false
      state.error = null
    },
    exportStaffsInCurrentPageFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Export selected staffs
    exportSelectedStaffs: (
      state,
      action: PayloadAction<{
        orgId: string
        userIds: string[]
        onSuccess?: (data: any) => void
        onFailure?: (error: string) => void
      }>,
    ) => {
      state.loading = true
      state.error = null
    },
    exportSelectedStaffsSuccess: (state) => {
      state.loading = false
      state.error = null
    },
    exportSelectedStaffsFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Import selected staffs
    importStaffs: (
      state,
      action: PayloadAction<{
        orgId: string
        file: File
        onSuccess?: (data: any) => void
        onFailure?: (error: string) => void
      }>,
    ) => {
      state.loading = true
      state.error = null
    },
    importStaffsSuccess: state => {
      state.loading = false
      state.error = null
    },
    importStaffsFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
  },
})

export const StaffActions = StaffSlice.actions
export const StaffReducer = StaffSlice.reducer
